import {
  IsBoolean,
  IsNotEmpty,
  IsOptional,
  IsUUID,
  ValidateNested,
} from 'class-validator';
import { Type } from 'class-transformer';
import { BaseDto } from '../base.dto';
import { FormSubmissionBlobDto, FormSubmissionDto } from '../forms';
import { UserDto } from '../user.dto';

/**
 * Opens the caller's response draft. `formTemplateVersionId` names the version
 * the client rendered, which the draft pins; when it is omitted (older clients)
 * the server resolves the assigned one from the referral. `submission` seeds the
 * new draft when the specialist edits an answer they already sent.
 */
export class CreateReferralResponseDto {
  @IsOptional()
  @ValidateNested()
  @Type(() => FormSubmissionBlobDto)
  submission?: FormSubmissionBlobDto;

  @IsOptional()
  @IsUUID()
  formTemplateVersionId?: string;
}

/** Autosaves the caller's draft, or completes it with `draft: false`. */
export class UpdateReferralResponseDto {
  @IsOptional()
  @ValidateNested()
  @Type(() => FormSubmissionBlobDto)
  submission?: FormSubmissionBlobDto;

  @IsOptional()
  @IsBoolean()
  draft?: boolean;
}

/**
 * The referring clinician's judgement of one completed answer. A review is a
 * single submit, not an autosaved draft, so the blob is required — there is no
 * body that stores a review grading nothing. `formTemplateVersionId` names the
 * review version the client rendered, which the blob is validated against and
 * pinned to; when it is omitted (older clients) the server resolves the assigned
 * one from the referral.
 */
export class CreateReferralResponseReviewDto {
  @IsNotEmpty()
  @ValidateNested()
  @Type(() => FormSubmissionBlobDto)
  submission: FormSubmissionBlobDto;

  @IsOptional()
  @IsUUID()
  formTemplateVersionId?: string;
}

/**
 * One review. The grade and the comment live in `formSubmission` against the
 * pinned review template, so this carries no answer fields of its own.
 *
 * Read back for the referring clinician's side only: the answering specialist is
 * never told how their answer was graded.
 */
export interface ReferralResponseReviewDto extends Partial<BaseDto> {
  id: string;
  referralResponseId: string;
  medicalUnitId: string;
  formSubmission: FormSubmissionDto;
  createdByUser?: UserDto;
}

/**
 * One specialist answer. The answer itself lives in `formSubmission`, whose
 * `draft` flag is the response's own draft state — there is no draft column on
 * the response.
 */
export interface ReferralResponseDto extends Partial<BaseDto> {
  id: string;
  referralId: string;
  medicalUnitId: string;
  draft: boolean;
  formSubmission: FormSubmissionDto;
  createdByUser?: UserDto;
  /**
   * The referring clinician's review of this answer, absent until one is given
   * *and* absent for every caller who may not read it — so a read that forgets
   * to ask for it cannot leak the grade to the specialist who was graded.
   */
  review?: ReferralResponseReviewDto;
}
